<script setup>
import ProductFeedVariableFields from '@frontJs/components/forms/projectManagement/sma/ProductFeedVariableFields.vue';
import ProductFeedItemTable from '@frontJs/components/tables/projectManagement/sma/ProductFeedItemTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayVariablesAction from '@js/components/actions/DisplayVariablesAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, h, reactive, ref } from 'vue';
import { string, object } from 'yup';

const {
    tenant,
    facebookAdsProductFeed,
    facebookAdsProductFeedTypes,
    facebookAdsProductFeedVariables,
    mappingVariableInformation,
    productAttributes,
} = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    facebookAdsProductFeed: {
        required: false,
        type: Object,
    },
    facebookAdsProductFeedTypes: {
        required: true,
        type: Array,
    },
    facebookAdsProductFeedVariables: {
        required: true,
        type: Array,
    },
    mappingVariableInformation: {
        required: false,
        type: Object,
    },
    productAttributes: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    type: facebookAdsProductFeed
        ? facebookAdsProductFeedTypes.find(
              (type) => type.value === facebookAdsProductFeed.type.value,
          )
        : facebookAdsProductFeedTypes[0],
    name: facebookAdsProductFeed ? facebookAdsProductFeed.name : '',
    mappings: facebookAdsProductFeed ? facebookAdsProductFeed.mappings : {},
    variables: initializeVariables(),
});

const isSubmitting = ref(false);

const selectedTab = ref();

const validationSchema = computed(() => {
    let schema = {
        type: object().required('Ce champ est obligatoire.'),
        name: string().required('Ce champ est obligatoire.'),
    };

    if (form.type.value === 'primary') {
        schema = {
            ...schema,
            mappings: object().shape({
                id: string().required('Ce champ est obligatoire.'),
                title: string().required('Ce champ est obligatoire.'),
                description: string().required('Ce champ est obligatoire.'),
                availability: string().required('Ce champ est obligatoire.'),
                condition: string().required('Ce champ est obligatoire.'),
                price: string().required('Ce champ est obligatoire.'),
                link: string().required('Ce champ est obligatoire.'),
                imageLink: string().required('Ce champ est obligatoire.'),
                brand: string().required('Ce champ est obligatoire.'),
                additionalImageLink: string().nullable(),
                googleProductCategory: string().nullable(),
                salePrice: string().nullable(),
                salePriceEffectiveDate: string().nullable(),
                itemGroupId: string().nullable(),
                color: string().nullable(),
                material: string().nullable(),
                shippingWeight: string().nullable(),
                customLabel0: string().nullable(),
                customLabel1: string().nullable(),
                customLabel2: string().nullable(),
                customLabel3: string().nullable(),
                customLabel4: string().nullable(),
            }),
        };
    }

    if (form.type.value === 'secondary') {
        schema = {
            ...schema,
            mappings: object().shape({
                id: string().required('Ce champ est obligatoire.'),
                title: string().nullable(),
                description: string().nullable(),
                imageLink: string().nullable(),
                brand: string().nullable(),
                additionalImageLink: string().nullable(),
                googleProductCategory: string().nullable(),
                itemGroupId: string().nullable(),
                color: string().nullable(),
                material: string().nullable(),
                customLabel0: string().nullable(),
                customLabel1: string().nullable(),
                customLabel2: string().nullable(),
                customLabel3: string().nullable(),
                customLabel4: string().nullable(),
            }),
        };
    }

    return object(schema);
});

function saveAction() {
    isSubmitting.value = true;

    const variables = {};

    for (const variable of facebookAdsProductFeedVariables) {
        const definition = form.variables[variable.value];

        const sanitized = {
            conditions: definition.conditions
                .filter(
                    (condition) =>
                        condition.value !== null &&
                        condition.value !== undefined &&
                        condition.value !== '',
                )
                .map((condition) => ({
                    metric: 'quantitySold',
                    period: condition.period,
                    months:
                        condition.months === null || condition.months === ''
                            ? 6
                            : parseInt(condition.months, 10),
                    operator: condition.operator,
                    value: parseInt(condition.value, 10),
                    logicalOperator: condition.logicalOperator,
                })),
            productAttributeFilters: buildProductAttributeFilters(
                definition.productAttributeFilters,
            ),
        };

        if (
            definition.quantityInStock.value !== null &&
            definition.quantityInStock.value !== undefined &&
            definition.quantityInStock.value !== ''
        ) {
            sanitized.quantityInStock = {
                operator: definition.quantityInStock.operator,
                value: parseInt(definition.quantityInStock.value, 10),
            };
        }

        variables[variable.value] = sanitized;
    }

    axios
        .post(
            route('front.projectManagement.sma.postProductFeed', {
                tenant: tenant.id,
                ...(facebookAdsProductFeed
                    ? { facebookAdsProductFeed: facebookAdsProductFeed.id }
                    : {}),
            }),
            {
                type: form.type.value,
                name: form.name,
                mappings: form.mappings,
                variables: variables,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.sma.productFeeds',
                    tenant ? { tenant: tenant.id } : {},
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.sma.productFeeds', {
                    tenant: tenant.id,
                }),
            );
        });
}

function initializeVariables() {
    const rawVariables =
        facebookAdsProductFeed &&
        facebookAdsProductFeed.variables &&
        !Array.isArray(facebookAdsProductFeed.variables)
            ? facebookAdsProductFeed.variables
            : {};

    const variables = {};

    for (const variable of facebookAdsProductFeedVariables) {
        variables[variable.value] = initializeVariableDefinition(
            rawVariables[variable.value] || {},
        );
    }

    return variables;
}

function initializeVariableDefinition(raw) {
    let conditions = [];

    if (Array.isArray(raw.conditions)) {
        conditions = raw.conditions.map((condition) => ({
            metric: 'quantitySold',
            period: condition.period || 'current',
            months: condition.months ?? 6,
            operator: condition.operator || '>=',
            value: condition.value ?? null,
            logicalOperator: condition.logicalOperator || 'and',
        }));
    }

    return {
        conditions,
        quantityInStock: {
            operator: raw.quantityInStock?.operator || '>=',
            value: raw.quantityInStock?.value ?? null,
        },
        productAttributeFilters: initializeProductAttributeFilters(
            raw.productAttributeFilters || {},
        ),
    };
}

function initializeProductAttributeFilters(rawAttributeFilters) {
    const productAttributeFilters = {};

    for (const [key, value] of Object.entries(rawAttributeFilters)) {
        const attribute = productAttributes.find((attr) => attr.code === key);

        if (attribute) {
            if (attribute.valueType === 'option') {
                productAttributeFilters[key] = (
                    Array.isArray(value) ? value : []
                )
                    .map((val) =>
                        attribute.options.find((opt) => opt.value === val),
                    )
                    .filter((opt) => opt !== undefined);
            } else {
                productAttributeFilters[key] = value;
            }
        }
    }

    return productAttributeFilters;
}

function buildProductAttributeFilters(attributeFilters) {
    const result = {};

    for (const [key, value] of Object.entries(attributeFilters)) {
        if (Array.isArray(value) && value.length > 0) {
            result[key] = value.map((item) => item.value);
        } else if (value !== null && value !== undefined && value !== '') {
            result[key] = value;
        }
    }

    return result;
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="saveAction"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                configuration: {
                    title: 'Configuration',
                    items: {
                        mappings: {
                            title: 'Mappings',
                            contentSlotName: 'mappings',
                            forceMount: true,
                        },
                        variables: {
                            title: 'Variables',
                            contentSlotName: 'variables',
                            forceMount: true,
                        },
                    },
                    forceMount: true,
                },
                ...(facebookAdsProductFeed
                    ? {
                          products: {
                              title: 'Produits',
                              content: h(ProductFeedItemTable, {
                                  tenant,
                                  facebookAdsProductFeed,
                                  facebookAdsProductFeedVariables,
                              }),
                              forceMount: false,
                          },
                      }
                    : {}),
            }"
        >
            <template #information>
                <FormContent>
                    <FormGrid>
                        <ListboxField
                            v-model="form.type"
                            field-id="type"
                            field-name="type"
                            label="Type"
                            :options="facebookAdsProductFeedTypes"
                            required
                        >
                            <template #buttonContent>
                                {{ form.type.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.name"
                            label="Nom"
                            field-name="name"
                            field-id="name"
                            type="text"
                            required
                        />
                    </FormGrid>
                </FormContent>
            </template>

            <template #mappings>
                <FormContent>
                    <div class="mb-4 flex justify-end">
                        <DisplayVariablesAction
                            title="Variables"
                            :variables="mappingVariableInformation"
                        >
                            <template #trigger>
                                <ActionButton type="secondary" size="small">
                                    Variables
                                </ActionButton>
                            </template>
                        </DisplayVariablesAction>
                    </div>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.id"
                            label="ID"
                            field-name="mappings.id"
                            field-id="mappings.id"
                            type="text"
                            required
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.title"
                            label="Titre"
                            field-name="mappings.title"
                            field-id="mappings.title"
                            type="text"
                            :required="form.type.value === 'primary'"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.description"
                            label="Description"
                            field-name="mappings.description"
                            field-id="mappings.description"
                            type="text"
                            :required="form.type.value === 'primary'"
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.availability"
                            label="Disponibilité"
                            field-name="mappings.availability"
                            field-id="mappings.availability"
                            type="text"
                            required
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.condition"
                            label="Condition"
                            field-name="mappings.condition"
                            field-id="mappings.condition"
                            type="text"
                            required
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.price"
                            label="Prix"
                            field-name="mappings.price"
                            field-id="mappings.price"
                            type="text"
                            required
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.link"
                            label="Lien"
                            field-name="mappings.link"
                            field-id="mappings.link"
                            type="text"
                            required
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.imageLink"
                            label="Lien image"
                            field-name="mappings.imageLink"
                            field-id="mappings.imageLink"
                            type="text"
                            :required="form.type.value === 'primary'"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.brand"
                            label="Marque"
                            field-name="mappings.brand"
                            field-id="mappings.brand"
                            type="text"
                            :required="form.type.value === 'primary'"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.additionalImageLink"
                            label="Lien image supplémentaire"
                            field-name="mappings.additionalImageLink"
                            field-id="mappings.additionalImageLink"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.googleProductCategory"
                            label="Catégorie produit Google"
                            field-name="mappings.googleProductCategory"
                            field-id="mappings.googleProductCategory"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.salePrice"
                            label="Prix soldé"
                            field-name="mappings.salePrice"
                            field-id="mappings.salePrice"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.salePriceEffectiveDate"
                            label="Date de début et de fin du prix soldé"
                            field-name="mappings.salePriceEffectiveDate"
                            field-id="mappings.salePriceEffectiveDate"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.itemGroupId"
                            label="ID groupe d'articles"
                            field-name="mappings.itemGroupId"
                            field-id="mappings.itemGroupId"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.color"
                            label="Couleur"
                            field-name="mappings.color"
                            field-id="mappings.color"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.material"
                            label="Matériau"
                            field-name="mappings.material"
                            field-id="mappings.material"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid v-if="form.type.value === 'primary'" :columns="2">
                        <InputField
                            v-model="form.mappings.shippingWeight"
                            label="Poids"
                            field-name="mappings.shippingWeight"
                            field-id="mappings.shippingWeight"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.customLabel0"
                            label="Étiquette personnalisée 0"
                            field-name="mappings.customLabel0"
                            field-id="mappings.customLabel0"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.customLabel1"
                            label="Étiquette personnalisée 1"
                            field-name="mappings.customLabel1"
                            field-id="mappings.customLabel1"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.customLabel2"
                            label="Étiquette personnalisée 2"
                            field-name="mappings.customLabel2"
                            field-id="mappings.customLabel2"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.customLabel3"
                            label="Étiquette personnalisée 3"
                            field-name="mappings.customLabel3"
                            field-id="mappings.customLabel3"
                            type="text"
                        />
                    </FormGrid>

                    <FormGrid :columns="2">
                        <InputField
                            v-model="form.mappings.customLabel4"
                            label="Étiquette personnalisée 4"
                            field-name="mappings.customLabel4"
                            field-id="mappings.customLabel4"
                            type="text"
                        />
                    </FormGrid>
                </FormContent>
            </template>

            <template #variables>
                <FormColumn>
                    <ProductFeedVariableFields
                        v-for="variable in facebookAdsProductFeedVariables"
                        :key="variable.value"
                        :definition="form.variables[variable.value]"
                        :variable="variable"
                        :product-attributes="productAttributes"
                    />
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter
            v-if="selectedTab !== 'products'"
            :is-submitting="isSubmitting"
            :errors="errors"
        >
            <template #actions>
                <DeleteAction
                    v-if="facebookAdsProductFeed"
                    :url="
                        route('front.projectManagement.sma.deleteProductFeed', {
                            tenant: tenant.id,
                            facebookAdsProductFeed: facebookAdsProductFeed.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.sma.productFeeds',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
